import { motion } from 'framer-motion';
import { cx } from '@twind/core';
import useModule from 'app/hooks/useModule';
import { ChatModuleType } from 'app/copied/models/ChatModules';
import TailorPrivacyBanner from 'app/modules/tailor/components/TailorPrivacyBanner';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomScrollbar from 'app/components/CustomScrollbar';
import useOpenOnScroll from 'app/hooks/useOpenOnScroll';
import { useRef, useEffect } from 'react';
import { shadowRoot } from 'app/shadowRoot';

import Header from '../../../components/Header';
import {
  ChatItem,
  ChatItemForm,
  ChatItemFormType,
  ChatItemMessage,
  ChatItemSourceType,
  ChatItemType,
} from '../../../services/chatBot';
import ChatItemFactory from '../components/chat-items/ChatItemFactory';
import Typing from '../../../components/Typing';
import BaseChatItem from '../components/chat-items/BaseChatItem';
import Spinner from '../../../components/Spinner';
import LiveChatPrompt from '../../live-chat/components/LiveChatPrompt';
import useVirtualAssistant from '../hooks/useVirtualAssistant';
import useVirtualAssistantAvatar from '../hooks/useVirtualAssistantAvatar';
import { VirtualAssistantApp } from '../VirtualAssistantApp';

const containerVariants = {
  hidden: {
    opacity: 0,
    originX: 0,
    originY: 'top',
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const isContactForm = (item: ChatItem) => {
  return item.type === ChatItemType.FORM && (item as ChatItemForm).form_type === ChatItemFormType.CONTACT_FORM;
};

export default function VirtualAssistant() {
  const location = useLocation();
  const { data: helpCenter } = useFetchHelpCenter();
  const vaModule = useModule(ChatModuleType.VirtualAssistant);
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useOpenOnScroll(scrollContainerRef);

  const {
    chatItems,
    isTyping,
    typingAreaAvailable,
    userMessage,
    isLoadingVirtualAssistant,
    userResponsePresent,
    visibleChatItems,
    chatContainerRef,
    answerResponse,
    setUserMessage,
    handleAnswer,
    handleDismiss,
    handleMessage,
    handleContentClick,
    handleDisplayFormItem,
    showTypingArea,
    resetScrollToBottomAfterInit,
    lastInitItemId,
  } = useVirtualAssistant();

  const avatarURL = useVirtualAssistantAvatar();

  const previousChatItemsRef = useRef(visibleChatItems);

  useEffect(() => {
    // Handle scroll to bottom after initChatBot
    if (lastInitItemId && chatContainerRef.current) {
      const lastInitItem = visibleChatItems.find((item) => item.uuid === lastInitItemId);
      if (lastInitItem?.visible) {
        const scrollElement = chatContainerRef.current;
        setTimeout(() => {
          scrollElement.scrollTo({
            top: scrollElement.scrollHeight,
            behavior: 'smooth',
          });
          resetScrollToBottomAfterInit();
        }, 100);
        return;
      }
    }

    const newMessages = visibleChatItems.filter((item) => {
      const isMessage = [ChatItemType.MESSAGE, ChatItemType.KB_ENTRY].includes(item.type);
      const isNew = !previousChatItemsRef.current.some((prevItem) => prevItem.uuid === item.uuid);

      return (isMessage || isContactForm(item)) && isNew;
    });

    const patientMessages = visibleChatItems.filter(
      (item) =>
        [ChatItemSourceType.PATIENT, ChatItemSourceType.PATIENT_CHOICE].includes(item.source) &&
        item.type === ChatItemType.MESSAGE
    );
    const latestPatientMessage = patientMessages[patientMessages.length - 1];
    const mostRecentMessage = newMessages[newMessages.length - 1];

    if (mostRecentMessage?.errorMessage) {
      const element = shadowRoot.getElementById(mostRecentMessage.uuid);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (newMessages.length > 0 && latestPatientMessage) {
      const element = shadowRoot.getElementById(latestPatientMessage.uuid);
      if (element) {
        setTimeout(
          () => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
          newMessages.some(isContactForm) ? 750 : 0
        );
      }
    }

    previousChatItemsRef.current = visibleChatItems;
  }, [visibleChatItems, chatContainerRef, resetScrollToBottomAfterInit, lastInitItemId]);

  const handleBackClick = () => {
    navigate('/tailor', { state: { isBack: true } });
  };

  if (isLoadingVirtualAssistant) {
    return (
      <div className="px-7 py-5">
        <Header />
        <div className="flex justify-center mt-16">
          <Spinner />
        </div>
      </div>
    );
  }
  return (
    <VirtualAssistantApp>
      <div className="flex grow flex-col h-0">
        <div className="px-7 py-5 relative">
          <Header
            onClickBack={handleBackClick}
            showBackButton={!vaModule?.va_as_landing_page_enabled}
          />
        </div>
        <CustomScrollbar
          id="ostro-va-chat-container"
          containerRef={chatContainerRef}
          className="px-7 pt-5"
          alignToBottom
        >
          {visibleChatItems.map((item, i) => {
            const hideAvatar =
              i > 0 &&
              visibleChatItems[i - 1].type !== ChatItemType.FORM &&
              visibleChatItems[i - 1].source !== ChatItemSourceType.PATIENT &&
              visibleChatItems[i - 1].source !== ChatItemSourceType.PATIENT_CHOICE;

            return (
              <motion.div
                id={item.uuid}
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                key={item.uuid}
              >
                <ChatItemFactory
                  shouldAnimate={i === visibleChatItems.length - 1}
                  className={cx(
                    i > 0 && !userResponsePresent ? 'flex-col [&>div:first-child]:hidden' : '',
                    i === 0 && !userResponsePresent ? 'flex-col' : '',
                    i === 0 && userResponsePresent ? '[&_h1]:text-sm' : ''
                  )}
                  hideAvatar={hideAvatar}
                  answerItem={
                    item.type === ChatItemType.FORM && answerResponse?.form?.ref_id === (item as ChatItemForm).ref_id
                      ? answerResponse.form
                      : undefined
                  }
                  chatItem={item}
                  onAnswer={handleAnswer}
                  onDismiss={handleDismiss}
                  onClickContent={handleContentClick}
                  onDisplayFormItem={handleDisplayFormItem}
                  previousUserMessage={
                    (
                      visibleChatItems
                        .slice(0, i)
                        .reverse()
                        .find(
                          (prevItem) =>
                            prevItem.type === ChatItemType.MESSAGE && prevItem.source === ChatItemSourceType.PATIENT
                        ) as ChatItemMessage
                    )?.message
                  }
                />
              </motion.div>
            );
          })}
          {isTyping && (
            <BaseChatItem
              avatarURL={avatarURL}
              className={!userResponsePresent ? '[&>div:first-child]:hidden' : ''}
              showAvatar={chatItems.length === 0}
            >
              <Typing />
            </BaseChatItem>
          )}
        </CustomScrollbar>
        {typingAreaAvailable && showTypingArea && (
          <LiveChatPrompt
            message={userMessage}
            disabled={false}
            onChange={setUserMessage}
            onSend={handleMessage}
          />
        )}
        {(vaModule?.va_as_landing_page_enabled || location.state?.source === 'nonWidget') && (
          <div className="shrink-0">
            <TailorPrivacyBanner footerText={helpCenter?.footer.lines} />
          </div>
        )}
      </div>
    </VirtualAssistantApp>
  );
}
